"use client";

// Floating-label form fields shared by every lead/contact form. The label sits
// inside the field like a placeholder and floats up to a small caption once the
// field is focused or filled (material style). Validation is per-field on blur
// and re-checked on submit via validateLeadFields(); errors render inline.
import {
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

/* ------------------------------------------------------------- validators */

export const validators = {
  required: (label: string) => (v: string) =>
    v.trim().length >= 2 ? null : `Please enter ${label.toLowerCase()}`,
  email: (v: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())
      ? null
      : "Please enter a valid email address",
  // 10-digit Indian mobiles (6-9 start), with optional +91/0 prefix; spaces,
  // dashes and brackets are ignored.
  mobile: (v: string) => {
    const digits = v.replace(/[\s()-]/g, "");
    return /^(\+91|0)?[6-9]\d{9}$/.test(digits)
      ? null
      : "Please enter a valid 10-digit mobile number";
  },
  optional: () => null as string | null,
};

export type FieldRule = (value: string) => string | null;

// Validate a form element's named fields; returns {name: error} and focuses
// the first failing field. Used by the submit handlers.
export function validateLeadFields(
  form: HTMLFormElement,
  rules: Record<string, FieldRule>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [name, rule] of Object.entries(rules)) {
    const el = form.elements.namedItem(name) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
      | null;
    if (!el) continue;
    const err = rule(el.value ?? "");
    if (err) errors[name] = err;
  }
  const first = Object.keys(errors)[0];
  if (first) {
    (form.elements.namedItem(first) as HTMLElement | null)?.focus();
  }
  return errors;
}

/* ------------------------------------------------------------ shared bits */

type Tone = "light" | "dark";

// Field shell: bordered box (light) or underline (dark band) with the label
// floating between placeholder position and the top caption position.
const shell: Record<Tone, string> = {
  light:
    "peer w-full rounded-lg border bg-white px-3 py-3 text-sm text-brand-ink outline-hidden transition-colors placeholder-transparent focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20",
  dark:
    "peer w-full border-b bg-transparent pb-2 pt-5 text-base text-white outline-hidden transition-colors placeholder-transparent focus:border-brand-accent-light",
};
const shellBorder: Record<Tone, { ok: string; bad: string }> = {
  light: { ok: "border-brand-line", bad: "border-red-400" },
  dark: { ok: "border-white/25", bad: "border-red-400" },
};
const labelCls: Record<Tone, string> = {
  // floated: the label sits ON the top border (notched-outline style) with a
  // white chip masking the border line behind it, like the reference design.
  light:
    "pointer-events-none absolute -top-2 left-2.5 rounded bg-white px-1 text-xs text-brand-muted transition-all " +
    "peer-placeholder-shown:top-3 peer-placeholder-shown:left-3 peer-placeholder-shown:bg-transparent peer-placeholder-shown:px-0 peer-placeholder-shown:text-sm " +
    "peer-focus:-top-2 peer-focus:left-2.5 peer-focus:bg-white peer-focus:px-1 peer-focus:text-xs peer-focus:text-brand-accent-dark",
  dark:
    "pointer-events-none absolute left-0 top-0 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/55 transition-all " +
    "peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-white/40 " +
    "peer-focus:top-0 peer-focus:text-[0.65rem] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-[0.12em] peer-focus:text-brand-accent-light",
};

function Err({ msg, tone }: { msg?: string; tone: Tone }) {
  if (!msg) return null;
  return (
    <p className={`mt-1.5 text-xs ${tone === "dark" ? "text-red-400" : "text-red-600"}`}>
      {msg}
    </p>
  );
}

type CommonProps = {
  label: string;
  tone?: Tone;
  rule?: FieldRule;
  error?: string;
  className?: string;
};

/* ---------------------------------------------------------------- fields */

export function FloatInput({
  label,
  tone = "light",
  rule,
  error: extError,
  className = "",
  onBlur,
  ...rest
}: CommonProps & InputHTMLAttributes<HTMLInputElement>) {
  const [error, setError] = useState<string | undefined>();
  const shown = extError ?? error;
  const id = rest.id ?? `ff-${rest.name}`;
  return (
    <div className={className}>
      <div className="relative">
        <input
          {...rest}
          id={id}
          placeholder={label}
          aria-invalid={!!shown}
          className={`${shell[tone]} ${shown ? shellBorder[tone].bad : shellBorder[tone].ok}`}
          onBlur={(e) => {
            if (rule) setError(rule(e.target.value) ?? undefined);
            onBlur?.(e);
          }}
        />
        <label htmlFor={id} className={labelCls[tone]}>
          {label}
        </label>
      </div>
      <Err msg={shown} tone={tone} />
    </div>
  );
}

export function FloatTextarea({
  label,
  tone = "light",
  rule,
  error: extError,
  className = "",
  onBlur,
  ...rest
}: CommonProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const [error, setError] = useState<string | undefined>();
  const shown = extError ?? error;
  const id = rest.id ?? `ff-${rest.name}`;
  return (
    <div className={className}>
      <div className="relative">
        <textarea
          {...rest}
          id={id}
          placeholder={label}
          aria-invalid={!!shown}
          className={`${shell[tone]} ${shown ? shellBorder[tone].bad : shellBorder[tone].ok}`}
          onBlur={(e) => {
            if (rule) setError(rule(e.target.value) ?? undefined);
            onBlur?.(e);
          }}
        />
        <label htmlFor={id} className={labelCls[tone]}>
          {label}
        </label>
      </div>
      <Err msg={shown} tone={tone} />
    </div>
  );
}

// Selects cannot show a transparent placeholder, so the label floats whenever
// a value is chosen (tracked in state) and sits in the field until then.
const selectLabelFloated: Record<Tone, string> = {
  light:
    "pointer-events-none absolute -top-2 left-2.5 rounded bg-white px-1 text-xs text-brand-muted transition-all",
  dark:
    "pointer-events-none absolute left-0 top-0 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/55 transition-all",
};
const selectLabelResting: Record<Tone, string> = {
  light:
    "pointer-events-none absolute left-3 top-3 text-sm text-brand-muted transition-all",
  dark:
    "pointer-events-none absolute left-0 top-5 text-base text-white/40 transition-all",
};

export function FloatSelect({
  label,
  tone = "light",
  error: extError,
  className = "",
  children,
  onChange,
  defaultValue,
  ...rest
}: CommonProps & SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const [hasValue, setHasValue] = useState(Boolean(defaultValue));
  const id = rest.id ?? `ff-${rest.name}`;
  return (
    <div className={className}>
      <div className="relative">
        {/* The select is text-transparent until something is chosen, so the
            floating label shows through the empty control. Options inherit
            that colour, which made the open dropdown list invisible until a
            row was hovered — so they set their own colour and background
            explicitly, on both tones. */}
        <select
          {...rest}
          id={id}
          defaultValue={defaultValue}
          aria-invalid={!!extError}
          className={`${shell[tone]} cursor-pointer appearance-none pr-8 ${
            extError ? shellBorder[tone].bad : shellBorder[tone].ok
          } [&>option]:bg-white [&>option]:text-brand-ink ${
            hasValue ? "" : "text-transparent"
          }`}
          onChange={(e) => {
            setHasValue(Boolean(e.target.value));
            onChange?.(e);
          }}
        >
          {children}
        </select>
        <label
          htmlFor={id}
          className={hasValue ? selectLabelFloated[tone] : selectLabelResting[tone]}
        >
          {label}
        </label>
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          className={`pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 ${
            tone === "dark" ? "text-white/50" : "text-brand-muted"
          }`}
        >
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <Err msg={extError} tone={tone} />
    </div>
  );
}
